import { Injectable, NgZone, inject } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { MessageService } from 'primeng/api'
import { filter } from 'rxjs'

import {
  TRACE_CAPACITY,
  TRACE_MAX_AGE_MS,
  TRACE_MAX_BYTES,
  TRACE_STORAGE_KEY,
  TRACE_VERSION,
  TraceContext,
  TraceEvent,
  TraceEventKind,
  TracePayload,
} from './trace-event.interface'
import { BODY_LIMITS, byteLength, fitEvent, redactText, redactValue } from './trace-redactor'
import { resolveScreen } from './screen-labels.constant'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { environment } from '@env/environment'

//* Cuánto se espera antes de escribir el buffer a sessionStorage. Serializar en cada paso se nota al
//* teclear; un segundo de retraso no pierde nada, porque además se vuelca al ocultar la pestaña.
const PERSIST_DEBOUNCE_MS = 1000

//* Dos clics idénticos dentro de esta ventana cuentan como uno con contador. Sin esto, un doble clic
//* nervioso sobre un botón que no responde —que es EXACTAMENTE lo que pasa cuando algo falla— vacía
//* el historial útil a fuerza de repetir la misma línea.
const CLICK_COALESCE_MS = 800

//* Un error que se repite dentro de esta ventana se registra una sola vez: zone.js y el listener de
//* `window` pueden ver el mismo fallo.
const ERROR_DEDUPE_MS = 2000

//* Elementos con los que de verdad se interactúa. Un clic que no cae dentro de uno de estos se
//* descarta sin más: es la regla que evita registrar el contenido de la pantalla al pasar el ratón.
const ACTIONABLE = 'button, a[href], [role="button"], .p-button, .p-menuitem-link, .p-dropdown-trigger, .p-paginator-page, th[psortablecolumn], label.wl-drop, label.bug-drop, .p-tabview-nav li'

//* Widgets que pintan el VALOR elegido como su texto. Se registra que se usó el control, nunca lo
//* que decía: en un desplegable de clientes, el texto es el nombre de una persona.
const VALUE_BEARING = ['.p-dropdown-label', '.p-autocomplete', '.p-chips-token-label', '.p-calendar', '.p-multiselect-label']

const MAX_LABEL_LENGTH = 60

/**
 * Bitácora de lo que hace quien usa el panel.
 *
 * Guarda los últimos {@link TRACE_CAPACITY} pasos en memoria y **no los manda a ningún lado** salvo
 * que la persona reporte un problema. Existe para cerrar la distancia entre cómo se cuenta un fallo
 * ("estaba en pagos y mi pago no pasó") y lo que hace falta para reproducirlo.
 *
 * Tres decisiones que conviene no deshacer:
 *
 * - **Se redacta al capturar, no al enviar.** Una credencial nunca llega a estar en memoria ni en
 *   `sessionStorage`, así que tampoco la vería alguien que abriera las herramientas del navegador.
 * - **Nunca rompe la aplicación.** Cada captura va en un `try/catch` mudo. Una herramienta de
 *   diagnóstico que tumba la pantalla que estaba observando es peor que no tenerla.
 * - **No se captura el teclado.** Lo que alguien escribe en un formulario es justo lo que no debe
 *   quedar registrado.
 */
@Injectable({ providedIn: 'root' })
export class TraceService {
  private readonly router = inject(Router)
  private readonly zone = inject(NgZone)
  private readonly auth = inject(AuthService)
  private readonly messageService = inject(MessageService)

  private buffer: TraceEvent[] = []
  private sizes: number[] = []
  private totalBytes = 0
  private seq = 0
  private started = false

  private screen = '/'
  private url = '/'
  private readonly sessionId = this.newId()
  private readonly startedAt = new Date().toISOString()

  private persistTimer: any = null
  private lastClick: { label: string; at: number; index: number } | null = null
  private lastError: { message: string; at: number } | null = null

  /** Arranca las fuentes. Lo llama un APP_INITIALIZER: sin esa llamada no hay bitácora. */
  start(): void {
    if (this.started) return
    this.started = true

    this.restore()
    this.watchSession()
    this.watchNavigation()
    this.watchClicks()
    this.watchErrors()
    this.watchToasts()
    this.watchLifecycle()

    this.screen = resolveScreen(this.router.url).pattern
    this.url = this.router.url
  }

  /** Agrega un paso. Redacta, acota y poda; nunca lanza. */
  push(kind: TraceEventKind, label: string, data?: Record<string, unknown>): void {
    try {
      const event = fitEvent({
        seq: ++this.seq,
        kind,
        at: Date.now(),
        label: redactText(label, MAX_LABEL_LENGTH * 3),
        screen: this.screen,
        data: data ? this.redactDetail(data) : undefined,
      })

      const size = byteLength(event)
      this.buffer.push(event)
      this.sizes.push(size)
      this.totalBytes += size

      //* Poda por los dos extremos: número de pasos y bytes. Un `shift()` sobre sesenta elementos es
      //* irrelevante, y llevar la suma corrida evita volver a serializar el buffer en cada paso.
      while (this.buffer.length > TRACE_CAPACITY || this.totalBytes > TRACE_MAX_BYTES) {
        this.buffer.shift()
        this.totalBytes -= this.sizes.shift() ?? 0
        if (this.lastClick) this.lastClick.index--
      }

      this.schedulePersist()
    } catch {
      //* En silencio a propósito: la bitácora no puede ser la causa de un fallo, y `console.*` con
      //* objetos de dominio está prohibido en este repo.
    }
  }

  /**
   * Redacta el detalle de un paso, tratando el CUERPO de la petición aparte.
   *
   * !! El cuerpo necesita más profundidad que el resto y, además, aquí gana un nivel: dentro de
   * `data` está un escalón más abajo que cuando lo redactó quien lo capturó. Pasándolo por los
   * límites de por defecto, los filtros del panel —`{ filters: { campo: { value, matchMode } } }`—
   * salían cortados en «‹…›» justo en el dato que se quería ver. Y era invisible: el cuerpo YA
   * venía redactado, así que parecía que los límites del cuerpo no se aplicaban.
   */
  private redactDetail(data: Record<string, unknown>): Record<string, unknown> {
    const { body, ...resto } = data
    const redactado = redactValue(resto) as Record<string, unknown>
    if (body === undefined) return redactado
    return { ...redactado, body: redactValue(body, BODY_LIMITS) }
  }

  /** Los últimos `n` pasos, en orden cronológico. Copia defensiva. */
  snapshot(n = TRACE_CAPACITY): TraceEvent[] {
    return this.buffer.slice(-n).map((e) => ({ ...e }))
  }

  /** El paquete completo listo para mandarse con un reporte. */
  payload(n = TRACE_CAPACITY): TracePayload {
    return { context: this.context(), events: this.snapshot(n) }
  }

  /** El entorno de esta pestaña. */
  context(): TraceContext {
    return {
      url: this.url,
      route: this.screen,
      sessionId: this.sessionId,
      appVersion: environment.appVersion,
      userAgent: navigator.userAgent ?? '',
      platform: (navigator as any).userAgentData?.platform ?? navigator.platform ?? '',
      language: navigator.language ?? '',
      //* Se invierte el signo: `getTimezoneOffset` devuelve los minutos que hay que SUMAR a la hora
      //* local para llegar a UTC, que es al revés de como lo lee cualquiera.
      utcOffsetMinutes: -new Date().getTimezoneOffset(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      startedAt: this.startedAt,
      capturedAt: new Date().toISOString(),
    }
  }

  clear(reason: 'login' | 'logout' | 'sent' | 'stale' | 'other'): void {
    this.buffer = []
    this.sizes = []
    this.totalBytes = 0
    this.lastClick = null
    if (reason === 'logout') this.forget()
    else this.schedulePersist()
  }

  /** El patrón de la pantalla actual, para componer el título de un reporte. */
  get currentScreen(): string {
    return this.screen
  }

  /** La URL real, con el id: es lo que permite volver a la pantalla exacta. */
  get currentUrl(): string {
    return this.url
  }

  /** El último paso que salió mal, si ocurrió hace poco. Alimenta el título automático. */
  lastFailure(maxAgeMs = 2 * 60 * 1000): TraceEvent | null {
    const limit = Date.now() - maxAgeMs
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const e = this.buffer[i]
      if (e.at < limit) return null
      if (e.kind === 'error') return e
      if (e.kind === 'http' && Number(e.data?.['status'] ?? 0) >= 400) return e
      if (e.kind === 'http' && Number(e.data?.['status']) === 0) return e
    }
    return null
  }

  // ─────────────────────────── Fuentes ───────────────────────────

  private watchNavigation(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const from = this.screen
      const screen = resolveScreen(e.urlAfterRedirects)
      this.screen = screen.pattern
      this.url = e.urlAfterRedirects
      //* La URL real con el id SÍ se guarda: un ObjectId no es un dato personal y sin él nadie puede
      //* volver a la pantalla donde ocurrió el fallo.
      this.push('nav', `Entró a «${screen.label}»`, { from, to: e.urlAfterRedirects })
    })
  }

  private watchClicks(): void {
    //! Fuera de la zona de Angular: un listener global dentro de la zona hace que zone.js dispare un
    //! ciclo de detección de cambios extra por CADA clic de toda la aplicación.
    this.zone.runOutsideAngular(() => {
      //* En fase de captura porque muchos botones del panel hacen `stopPropagation()`; en burbujeo
      //* se perderían justo los de los diálogos.
      document.addEventListener('click', (event) => this.onClick(event), { capture: true, passive: true })
    })
  }

  private onClick(event: MouseEvent): void {
    try {
      const target = event.target as HTMLElement | null
      if (!target?.closest) return
      if (target.closest('[data-trace-off]')) return
      //! Los clics DENTRO del reporte de problemas no entran a la bitácora. Sin esto, los últimos
      //! pasos —los más valiosos, los que explican el fallo— quedaban ocupados por "Clic en Enviar
      //! reporte" y "Clic en Reportar un problema", que no le dicen nada a quien va a arreglarlo.
      if (target.closest('.bug-report-dialog, .bug-tab')) return

      const label = this.labelForClick(target)
      if (!label) return

      const now = Date.now()
      if (this.lastClick && this.lastClick.label === label && now - this.lastClick.at < CLICK_COALESCE_MS) {
        const previous = this.buffer[this.lastClick.index]
        if (previous) {
          const count = Number(previous.data?.['count'] ?? 1) + 1
          previous.data = { ...(previous.data ?? {}), count }
          previous.label = `${label} ×${count}`
          this.lastClick.at = now
          return
        }
      }

      this.push('click', label)
      this.lastClick = { label, at: now, index: this.buffer.length - 1 }
    } catch {
      //* Un clic nunca puede fallar por culpa de la bitácora.
    }
  }

  /** Qué se escribe de un clic. Devuelve null cuando no se hizo clic en nada accionable. */
  private labelForClick(target: HTMLElement): string | null {
    //* Escotilla para que una pantalla ponga una etiqueta buena sin tocar este servicio.
    const marked = target.closest('[data-trace]') as HTMLElement | null
    if (marked?.dataset?.['trace']) return `Clic en «${marked.dataset['trace'].slice(0, MAX_LABEL_LENGTH)}»`

    const el = target.closest(ACTIONABLE) as HTMLElement | null
    //! Sin elemento accionable no se registra nada. Es la regla que evita capturar el contenido de
    //! la pantalla: un clic en una celda de una tabla de clientes no produce ningún paso.
    if (!el) return null

    for (const selector of VALUE_BEARING) {
      if (el.matches(selector) || el.querySelector(selector)) return 'Usó un control de selección'
    }

    //* Si el control envuelve un campo de texto, su `textContent` incluiría lo escrito.
    const wrapsInput = !!el.querySelector('input, textarea, select, [contenteditable]')
    const raw = el.getAttribute('aria-label') || el.getAttribute('title') || (wrapsInput ? el.getAttribute('placeholder') : el.textContent) || ''

    const text = raw.replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL_LENGTH)
    if (!text) return el.tagName === 'A' ? 'Clic en un enlace' : 'Clic en un botón'
    return `Clic en «${text}»`
  }

  private watchErrors(): void {
    this.zone.runOutsideAngular(() => {
      //* `window.error` cubre además los recursos que no cargan, que no pasan por el ErrorHandler.
      window.addEventListener('error', (e) => this.recordError(e.error ?? e.message, 'Error en pantalla'))
      window.addEventListener('unhandledrejection', (e) => this.recordError((e as PromiseRejectionEvent).reason, 'Promesa sin atender'))
    })
  }

  /** Registra un error de JavaScript. Público porque el ErrorHandler global también lo llama. */
  recordError(error: any, fallback = 'Error en pantalla'): void {
    try {
      const message = String(error?.message ?? error ?? fallback)
      const now = Date.now()
      if (this.lastError && this.lastError.message === message && now - this.lastError.at < ERROR_DEDUPE_MS) return
      this.lastError = { message, at: now }

      const name = String(error?.name ?? 'Error')
      //* Solo las primeras líneas del stack: es lo de más valor para quien arregla, y no contiene
      //* datos de negocio.
      const stack = String(error?.stack ?? '')
        .split('\n')
        .slice(0, 3)
        .join('\n')

      this.push('error', `${name}: ${message}`.slice(0, 160), { name, message, stack })
    } catch {
      //* Un fallo del registrador de fallos no puede escalar.
    }
  }

  private watchToasts(): void {
    //* Es lo que la persona VIO. Cobertura parcial conocida y aceptada: decenas de componentes del
    //* panel proveen su propia instancia de MessageService, así que aquí llegan los de quien usa la
    //* global — entre ellos el «Sin Permisos» del ErrorInterceptor, que es el más valioso. Perseguir
    //* el resto costaría tocar esos componentes uno por uno para ganar poco: los fallos detrás de
    //* esos avisos ya quedan registrados por la fuente de peticiones.
    this.messageService.messageObserver.subscribe((message: any) => {
      const list = Array.isArray(message) ? message : [message]
      for (const m of list) {
        if (!m) continue
        this.push('toast', `Vio el aviso: ${[m.summary, m.detail].filter(Boolean).join(' — ')}`.slice(0, 200), { severity: m.severity })
      }
    })
  }

  private watchSession(): void {
    let previous = this.auth.getUser?.user?.id ?? null
    this.auth.currentUser.subscribe((user) => {
      const id = user?.user?.id ?? null
      if (id === previous) return
      if (!id) {
        this.clear('logout')
      } else {
        this.clear('login')
        this.push('session', 'Inició sesión')
      }
      previous = id
    })
  }

  private watchLifecycle(): void {
    this.zone.runOutsideAngular(() => {
      //* `pagehide` y `visibilitychange` en vez de `beforeunload`, que los navegadores no garantizan.
      window.addEventListener('pagehide', () => this.persist())
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.persist()
      })
    })
  }

  // ─────────────────────────── Persistencia ───────────────────────────

  private schedulePersist(): void {
    if (this.persistTimer) return
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null
      this.persist()
    }, PERSIST_DEBOUNCE_MS)
  }

  /**
   * Vuelca el buffer a `sessionStorage`.
   *
   * `sessionStorage` y no `localStorage`: sobrevive a un F5 —que es el caso real, porque un fallo
   * suele terminar en recarga— pero no al cierre de la pestaña ni queda en el disco de un equipo
   * compartido.
   */
  private persist(): void {
    try {
      const uid = this.auth.getUser?.user?.id ?? null
      if (!uid) return
      sessionStorage.setItem(TRACE_STORAGE_KEY, JSON.stringify({ v: TRACE_VERSION, uid, savedAt: Date.now(), events: this.buffer }))
    } catch {
      //* Cuota llena o almacenamiento bloqueado: la bitácora sigue viva en memoria.
    }
  }

  private restore(): void {
    try {
      const raw = sessionStorage.getItem(TRACE_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      const uid = this.auth.getUser?.user?.id ?? null

      //! Se descarta si cambió el formato, si el paquete es viejo o —sobre todo— si es de OTRA
      //! persona: en un equipo compartido, restaurarlo mandaría el reporte de alguien con los pasos
      //! de quien usó la máquina antes.
      const usable = parsed?.v === TRACE_VERSION && !!uid && parsed?.uid === uid && Date.now() - Number(parsed?.savedAt ?? 0) < TRACE_MAX_AGE_MS
      if (!usable) {
        this.forget()
        return
      }

      this.buffer = Array.isArray(parsed.events) ? parsed.events : []
      this.sizes = this.buffer.map((e) => byteLength(e))
      this.totalBytes = this.sizes.reduce((a, b) => a + b, 0)
      this.seq = this.buffer.length ? Math.max(...this.buffer.map((e) => e.seq ?? 0)) : 0
      if (this.buffer.length) this.push('session', 'Recargó la página')
    } catch {
      this.forget()
    }
  }

  private forget(): void {
    try {
      sessionStorage.removeItem(TRACE_STORAGE_KEY)
    } catch {
      //* Sin almacenamiento no hay nada que olvidar.
    }
  }

  private newId(): string {
    try {
      return crypto.randomUUID()
    } catch {
      return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    }
  }
}
